import type { TUseAgCellRowDataRefresh } from 'components/AgGrid/types';
import type {
    ContractStatusEnum,
    WorkOrderPriceItem,
    WorkOrderSeasonData,
    WorkOrderVersionSeasonData,
} from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { sortLinkedActs } from '../../WorkOrderPrice/hooks';
import { SeasonCostRenderer, SeasonVolumeRenderer } from '../cellRenderers';
import { getSeasonValueByKey } from '../helpers';

interface IUseSeasonColumnsProps {
    actsColumns: ((ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>) & {
        id?: number;
        seasonId?: number;
    })[];
    isDisabledByProjectStatus: boolean;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    contractStatus: ContractStatusEnum | undefined;
    hasInProgress?: boolean;
    costRounding: number;
    seasons: (WorkOrderSeasonData | WorkOrderVersionSeasonData)[];
    useAgCellRowDataRefresh?: TUseAgCellRowDataRefresh;
    useAgCellRowDataRefreshParams?: any;
}

export const useWorkOrderSeasonColumns = (props: IUseSeasonColumnsProps) => {
    const {
        workOrderStatus,
        actsColumns,
        contractStatus,
        isDisabledByProjectStatus,
        hasInProgress,
        costRounding,
        seasons,
        useAgCellRowDataRefresh,
        useAgCellRowDataRefreshParams,
    } = props;

    const withFactAndRemainderColumns =
        workOrderStatus &&
        ([WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(workOrderStatus) ||
            (workOrderStatus === WorkOrderStatusEnum.Delayed && hasInProgress));

    return useMemo(
        () =>
            seasons.reduce((acc: (ColDef | ColGroupDef)[], { id, year }, index: number) => {
                const isOddSeason = !(index % 2);

                const cellClass = isOddSeason ? 'oddSeason' : 'evenSeason';

                const seasonPlanColumn: ColDef | ColGroupDef = {
                    headerName: `Сезон ${year}${withFactAndRemainderColumns ? ', План' : ''}`,
                    marryChildren: true,
                    headerClass: `${cellClass} flexStart`,
                    children: [
                        {
                            headerName: 'Объем',
                            cellClass: `ag-right-aligned-cell ${cellClass}`,
                            headerClass: cellClass,
                            width: 112,
                            cellRenderer: SeasonVolumeRenderer,
                            cellRendererParams: {
                                seasonId: id,
                                valueKey: 'planVolume',
                                statusContract: contractStatus,
                                disabledByProjectStatus: isDisabledByProjectStatus,
                                toolTipValueKey: 'planTerms',
                                workOrderStatus,
                                useAgCellRowDataRefresh,
                                useAgCellRowDataRefreshParams,
                            },
                            valueGetter: getSeasonValueByKey({
                                valueKey: 'planVolume',
                                seasonId: id,
                                costRounding: 3,
                                maxCostRoundingDigits: 6,
                            }),
                        },
                        {
                            headerName: 'Стоимость, руб. без НДС',
                            colId: `Сезон ${year}${withFactAndRemainderColumns ? ', План' : ''}`,
                            cellClass: `ag-right-aligned-cell ${cellClass}`,
                            headerClass: cellClass,
                            width: 130,
                            cellRenderer: SeasonCostRenderer,
                            cellRendererParams: {
                                seasonId: id,
                            },
                            valueGetter: getSeasonValueByKey({
                                valueKey: 'planCost',
                                seasonId: id,
                                costRounding,
                            }),
                        },
                    ],
                };

                const factAndRemainderColumns = withFactAndRemainderColumns
                    ? [
                          {
                              headerName: `Сезон ${year}, Выполнено`,
                              marryChildren: true,
                              headerClass: `${cellClass} flexStart`,
                              children: [
                                  {
                                      headerName: 'Объем',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                      valueFormatter: getSeasonValueByKey({
                                          valueKey: 'completedVolume',
                                          costRounding: 3,
                                          seasonId: id,
                                          maxCostRoundingDigits: 6,
                                      }),
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      colId: `Сезон ${year}, Выполнено`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 130,
                                      valueFormatter: getSeasonValueByKey({
                                          valueKey: 'completedCost',
                                          costRounding,
                                          seasonId: id,
                                      }),
                                  },
                              ],
                          },
                          {
                              headerName: `Сезон ${year}, Остаток`,
                              marryChildren: true,
                              headerClass: `${cellClass} flexStart`,
                              children: [
                                  {
                                      headerName: 'Объем',
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 112,
                                      valueFormatter: getSeasonValueByKey({
                                          valueKey: 'remainderVolume',
                                          costRounding: 3,
                                          seasonId: id,
                                          maxCostRoundingDigits: 6,
                                      }),
                                  },
                                  {
                                      headerName: 'Стоимость, руб. без НДС',
                                      colId: `Сезон ${year}, Остаток`,
                                      cellClass: `ag-right-aligned-cell ${cellClass}`,
                                      headerClass: cellClass,
                                      width: 130,
                                      valueFormatter: getSeasonValueByKey({
                                          valueKey: 'remainderCost',
                                          costRounding,
                                          seasonId: id,
                                      }),
                                  },
                              ],
                          },
                      ]
                    : [];

                const linkedActs = sortLinkedActs(actsColumns.filter((a) => a.seasonId === id));

                return acc.concat(seasonPlanColumn, factAndRemainderColumns, linkedActs);
            }, []),
        [
            actsColumns,
            contractStatus,
            costRounding,
            isDisabledByProjectStatus,
            seasons,
            useAgCellRowDataRefresh,
            useAgCellRowDataRefreshParams,
            withFactAndRemainderColumns,
            workOrderStatus,
        ],
    );
};
